/**
 * @file useNodeTypeHelpers.js
 * @description Node type checking and classification helper functions for BRICK Schema Manager
 *
 * This composable provides utilities to identify and classify different types of nodes
 * in the BRICK Schema hierarchy including equipment types, compound structures, and zones.
 */

/**
 * Node Type Helpers Composable
 *
 * Provides type checking functions for nodes in the BRICK Schema tree structure.
 * These functions help identify equipment types, compound structures, zones, and switchboards.
 *
 * @returns {Object} Object containing all node type helper functions
 */
export function useNodeTypeHelpers() {

  // ============================================================================
  // COMPOUND EQUIPMENT HELPERS
  // ============================================================================

  /**
   * Check if an equipment node is a compound equipment
   *
   * NOTE: Equipment compounds are no longer supported in the current system.
   * This function is kept for backwards compatibility and always returns false.
   *
   * @param {Object} node - The equipment node to check
   * @returns {boolean} Always returns false (equipment compounds deprecated)
   */
  const isCompoundEquipment = (node) => {
    return false; // Equipment compounds are no longer supported
  };

  /**
   * Get tooltip text for compound equipment
   *
   * NOTE: Equipment compounds are no longer supported in the current system.
   * This function is kept for backwards compatibility and always returns empty string.
   *
   * @param {Object} node - The equipment node
   * @returns {string} Always returns empty string (equipment compounds deprecated)
   */
  const getCompoundTooltip = (node) => {
    return ''; // Equipment compounds are no longer supported
  };

  // ============================================================================
  // COMPOUND DEVICE HELPERS
  // ============================================================================

  /**
   * Check if a device node is a compound device
   *
   * A compound device is a logical grouping of multiple physical devices that work together
   * as a single functional unit (e.g., multiple DDC controllers working as a system).
   *
   * @param {Object} node - The device node to check
   * @param {string} node.type - Should be 'device' for device nodes
   * @param {Object} [node.data] - Optional data object containing device properties
   * @param {boolean} [node.data.is_compound] - Flag indicating compound status
   * @param {boolean} [node.isCompound] - Alternative compound flag location
   * @returns {boolean} True if the device is a compound device, false otherwise
   */
  const isCompoundDevice = (node) => {
    if (!node || node.type !== 'device') {return false;}
    return !!(node.data?.is_compound || node.isCompound);
  };

  /**
   * Get tooltip text for compound devices
   *
   * Generates descriptive tooltip text showing the number of component devices
   * contained within the compound device.
   *
   * @param {Object} node - The device node
   * @param {Object} [node.data] - Optional data object
   * @param {number} [node.data.component_count] - Number of component devices
   * @param {number} [node.componentCount] - Alternative component count location
   * @returns {string} Tooltip text (e.g., "Compound device with 3 components") or empty string
   */
  const getCompoundDeviceTooltip = (node) => {
    if (!isCompoundDevice(node)) {return '';}
    const componentCount = node.data?.component_count || node.componentCount || 0;
    return `Compound device with ${componentCount} components`;
  };

  // ============================================================================
  // HVAC EQUIPMENT TYPE CHECKERS
  // ============================================================================

  /**
   * Check if an equipment node is an Air Handling Unit (AHU)
   *
   * Identifies AHU equipment by checking the BRICK class. Explicitly excludes
   * zone sections (AHU_Zone_Section) which are related but distinct components.
   *
   * @param {Object} node - The equipment node to check
   * @param {string} node.type - Should be 'equipment' for equipment nodes
   * @param {Object} [node.data] - Optional data object
   * @param {string} [node.data.brick_class] - BRICK Schema class identifier
   * @param {string} [node.brickClass] - Alternative BRICK class location
   * @returns {boolean} True if the equipment is an AHU (excluding zone sections)
   */
  const isAHU = (node) => {
    if (!node || node.type !== 'equipment') {return false;}
    const brickClass = node.data?.brick_class || node.brickClass || '';

    // Explicitly exclude zone sections (AHU_Zone_Section)
    if (brickClass === 'AHU_Zone_Section' || brickClass.toLowerCase().includes('zone_section')) {
      return false;
    }

    // Check if it's an actual AHU
    return brickClass.toLowerCase().includes('ahu') ||
           brickClass.toLowerCase().includes('air_handling_unit') ||
           brickClass.toLowerCase().includes('airhandlingunit');
  };

  /**
   * Check if an equipment node is a Zone Section
   *
   * Zone sections are specific components of an AHU that deliver conditioned air
   * to specific zones. They are identified by the BRICK class 'AHU_Zone_Section'.
   *
   * @param {Object} node - The equipment node to check
   * @param {string} node.type - Should be 'equipment' for equipment nodes
   * @param {Object} [node.data] - Optional data object
   * @param {string} [node.data.brick_class] - BRICK Schema class identifier
   * @param {string} [node.brickClass] - Alternative BRICK class location
   * @returns {boolean} True if the equipment is a zone section
   */
  const isZoneSection = (node) => {
    if (!node || node.type !== 'equipment') {return false;}
    const brickClass = node.data?.brick_class || node.brickClass || '';
    return brickClass === 'AHU_Zone_Section';
  };

  /**
   * Check if equipment is a Variable Air Volume (VAV) box or Terminal Unit
   *
   * VAVs and terminal units are end devices in HVAC systems that regulate
   * air flow to individual zones or spaces.
   *
   * @param {Object} equipment - The equipment object to check
   * @param {string} [equipment.brick_class] - BRICK Schema class identifier
   * @param {Object} [equipment.data] - Optional data object
   * @param {string} [equipment.data.brick_class] - Alternative BRICK class location
   * @returns {boolean} True if the equipment is a VAV or terminal unit
   */
  const isVAVOrTerminalUnit = (equipment) => {
    if (!equipment) {return false;}
    const brickClass = equipment.brick_class || equipment.data?.brick_class || '';
    return brickClass.includes('VAV') ||
           brickClass.includes('Variable_Air_Volume') ||
           brickClass.includes('Terminal_Unit') ||
           brickClass.includes('brick:Variable_Air_Volume_Box');
  };

  /**
   * Check if a node is a Zone
   *
   * Zones are terminal units or areas that receive conditioned air from AHU equipment.
   * This function checks both primary zone types and fallback space types with zone properties.
   *
   * CRITICAL: Zones are HVAC equipment (like terminal units connected to AHUs), NOT spaces/rooms.
   * - Zones appear UNDER AHU equipment in the tree (e.g., "Zone 1" under AHU B-1-1)
   * - Spaces with "zone" in the name are NOT zones (e.g., "HVAC Test Zone" is a space)
   *
   * @param {Object} node - The node to check
   * @param {string} node.type - Node type ('zone' or 'space')
   * @param {Object} [node.data] - Optional data object
   * @param {Object} [node.data.properties] - Node properties
   * @param {string} [node.data.properties.zone_type] - Zone type identifier
   * @param {Object} [node.data.metadata] - Node metadata
   * @param {string} [node.data.metadata.zone_type] - Alternative zone type location
   * @returns {boolean} True if the node is a zone (primary check) or space with zone_type (fallback)
   */
  const isZone = (node) => {
    // Check if it's a zone type node (PRIMARY CHECK)
    if (node && node.type === 'zone') {
      return true;
    }

    // Also check spaces with zone_type (FALLBACK)
    if (node && node.type === 'space') {
      const properties = node.data?.properties || node.properties || {};
      const metadata = node.data?.metadata || node.metadata || {};
      const hasZoneType = !!(properties.zone_type || metadata.zone_type);
      return hasZoneType;
    }

    return false;
  };

  // ============================================================================
  // ELECTRICAL TYPE CHECKERS
  // ============================================================================

  /**
   * Get human-readable label for switchboard type
   *
   * Converts switchboard type codes into user-friendly display labels.
   *
   * @param {string} type - Switchboard type code (e.g., 'MSB', 'MSSB', 'DDC_Panel')
   * @returns {string} Human-readable label for the switchboard type
   *
   * @example
   * getSwitchboardTypeLabel('MSB') // Returns 'Main Switchboard'
   * getSwitchboardTypeLabel('MSSB') // Returns 'Mechanical Services Switchboard'
   * getSwitchboardTypeLabel('DDC_Panel') // Returns 'DDC Panel'
   * getSwitchboardTypeLabel('unknown') // Returns 'unknown' (fallback)
   */
  const getSwitchboardTypeLabel = (type) => {
    const labels = {
      MSB: 'Main Switchboard',
      MSSB: 'Mechanical Services Switchboard',
      DDC_Panel: 'DDC Panel',
      DDC_Controller: 'DDC Controller',
    };
    return labels[type] || type;
  };

  // ============================================================================
  // TEMPLATE HELPERS
  // ============================================================================

  /**
   * Get components from a compound device template
   *
   * Retrieves the list of component definitions from a compound template.
   * This is used when instantiating compound devices from templates.
   *
   * @param {number|string} templateId - The ID of the compound template
   * @param {Array} compoundTemplates - Array of available compound templates
   * @returns {Array} Array of component definitions, or empty array if template not found
   *
   * Note: This function requires the compoundTemplates array to be passed in,
   * as it needs access to the template data store.
   */
  const getTemplateComponents = (templateId, compoundTemplates) => {
    const template = compoundTemplates.find((t) => t.id === templateId);
    if (!template) {return [];}

    return template.components || [];
  };

  // ============================================================================
  // EXPORTS
  // ============================================================================

  return {
    // Compound equipment helpers
    isCompoundEquipment,
    getCompoundTooltip,

    // Compound device helpers
    isCompoundDevice,
    getCompoundDeviceTooltip,

    // HVAC equipment type checkers
    isAHU,
    isZoneSection,
    isVAVOrTerminalUnit,
    isZone,

    // Electrical type checkers
    getSwitchboardTypeLabel,

    // Template helpers
    getTemplateComponents,
  };
}
